import { ApolloError } from "@apollo/client";
import { Mutation, UseCases } from "@aptus/frontend-core";
import event from "event";
import { Success } from "hooks/countries/models/country";
import { t } from "i18next";
import { InternalAPI } from "models/internalAPI";
import { useState } from "react";
import { Grape } from "./models/grape";
import { CreateGrapeInput, DeleteGrapeInput, SaveGrapeInput, UpdateGrapeInput } from "./models/grapeInput";

export type GrapesAPI = InternalAPI<Grape[]>;

export type UpdateGrapeMutation = Mutation<UpdateGrapeInput, Grape>;
export type CreateGrapeMutation = Mutation<CreateGrapeInput, Grape>;
export type DeleteGrapeMutation = Mutation<DeleteGrapeInput, Success>;

interface Props {
	API: GrapesAPI;
	defaultGrape: Grape;
	updateMutation: UpdateGrapeMutation;
	createMutation: CreateGrapeMutation;
	deleteMutation: DeleteGrapeMutation;
}
interface Result {
	grape: Grape;
	grapes: Grape[];
	isLoading: boolean;
	error?: Error;
	save: (input: SaveGrapeInput) => Promise<void>;
	remove: (id: string) => Promise<void>;
}

export const useGrapesUseCases: UseCases<Props, Result> = ({ API, defaultGrape, updateMutation, createMutation, deleteMutation }) => {
	const [isLoading, setIsLoading] = useState<Result["isLoading"]>(false);
	const isUpdate = (input: SaveGrapeInput): input is UpdateGrapeInput => !!input.id;

	const save: Result["save"] = async (input) => {
		setIsLoading(true);
		try {
			if (isUpdate(input)) {
				await updateMutation(input);
				event.emit("mutationSucceeded", t("grapeHasBeenSuccessfullyUpdated"));
			} else {
				await createMutation(input);
				event.emit("mutationSucceeded", t("grapeHasBeenSuccessfullyCreated"));
			}
		} catch (error) {
			if (error instanceof ApolloError) {
				event.emit("mutationFailed", error);
			}
		}
		setIsLoading(false);
	};

	const remove = async (id: string) => {
		try {
			await deleteMutation({ id });
			event.emit("mutationSucceeded", t("grapeHasBeenSuccessfullyDeleted"));
		} catch (error) {
			if (error instanceof ApolloError) {
				event.emit("mutationFailed", error);
			}
		}
	};

	return {
		grape: API.data[0] || defaultGrape,
		grapes: API.data || [],
		isLoading: API.isLoading || isLoading,
		save,
		remove,
	};
};
